/// <amd-module name="Test/Analytics/Widgets/PointHistoryWidget/PointHistoryInnerWidget" />
import * as React from "react";

import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import { DateSKParser } from "Analytics/Shared/Utils/DateSKParser";

import { ChartProps, ChartTypesConstants, DataSeries } from "VSS/Features/Charts/Contracts";
import { Card } from "VSSUI/Card";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Chart } from "VSS/Features/Charts/Chart";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import * as Date_Utils from "VSSUI/Utilities/Date";
import { ObservableValue } from "VSS/Core/Observable";
import { DatePicker } from "VSSUI/DatePicker";
import { Icon } from "VSSUI/Icon";

import * as Resources from "Test/Analytics/Widgets/PointHistoryWidget/Resources";
import { OutcomeToColorMap } from "Test/ManualTestReports/TestReportsCommon/Constants";
import { ITestProgressReportWidgetProps } from "Test/ManualTestReports/TestReportsCommon/ITestProgressReportWidgetProps";
import { BaseWidgetComponent } from "Test/ManualTestReports/TestReportsCommon/BaseWidgetComponent";
import { IPointHistoryOutcomeTrendState } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { PointHistoryCommands } from "Test/Analytics/Widgets/PointHistoryWidget/PointHistoryWidgetConstants";

export interface IPointHistoryInnerWidgetProps extends ITestProgressReportWidgetProps {
    loading: boolean;
    errorText?: string;
    historyData: IPointHistoryOutcomeTrendState;
    selectedCommand: PointHistoryCommands;
    fromDate: ObservableValue<Date>;
    toDate: ObservableValue<Date>;
    onCommandBarSubMenuItemSelected: (command: PointHistoryCommands) => void;
}

export interface IPointHistoryInnerWidgetState {
}

export class PointHistoryInnerWidget extends BaseWidgetComponent<IPointHistoryInnerWidgetProps, IPointHistoryInnerWidgetState, IVssComponentContext> {
    protected isLoading(): boolean {
        return this.props.loading;
    }

    constructor(props: IPointHistoryInnerWidgetProps) {
        super(props);
    }

    public render() {
        let elementToDisplay = (): JSX.Element => {
            if (this.props.loading === true) {
                return <Spinner className="flex-grow" size={SpinnerSize.large} />;
            }
            else if (this.props.errorText) {
                return <AnalyticsUnavailableMessage suggestion={this.props.errorText} />;
            }
            else if (this.props.historyData && this.props.historyData.Dates.length > 0) {
                return <div className="flex-column flex-grow" style={{width: "100%"}}>
                    <Chart {...this._getChartOptions()} />
                </div>;
            }
            return <div className="flex-row flex-center flex-grow ph-data-not-available" style={{width: "100%"}}>
                <div className="flex-center flex-column flex-grow">
                    <Icon iconName="Diagnostic" className={"icon-container flex-row"}></Icon>
                    <div className="flex-row title-s">
                        {Resources.DataNotAvailableText}
                    </div>
                </div>
            </div>;
        };

        return <Card className="point-history-widget-container flex-grow flex-column" titleProps={{ text: Resources.WidgetTitle, className: "title-s" }}
            headerCommandBarItems={this._getCommandBarItems(this.props.selectedCommand)}>
            {elementToDisplay()}
        </Card>;
    }

    private _getChartOptions(): ChartProps {
        var dates: string[] = [];
        this.props.historyData.Dates.forEach(date => {
            dates.push(DateSKParser.parseDateSKAsDateString(date));
        });
        let series: DataSeries[] = [];
        let addSeries = (counts: number[], name: string, color: string) => {
            // Show a series only if data is present
            if (counts && counts.some && counts.some((count: number) => { return count > 0 })) {
                series.push({
                    name: name,
                    data: counts,
                    color: color
                })
            }
        }
        addSeries(this.props.historyData.NotExecutedCounts, Resources.NotRunName, OutcomeToColorMap.NotExecuted);
        addSeries(this.props.historyData.PassedCounts, Resources.PassedName, OutcomeToColorMap.Passed);
        addSeries(this.props.historyData.FailedCounts, Resources.FailedName, OutcomeToColorMap.Failed);
        addSeries(this.props.historyData.NotApplicableCounts, Resources.NotApplicableName, OutcomeToColorMap.NotApplicable);
        addSeries(this.props.historyData.BlockedCounts, Resources.BlockedName, OutcomeToColorMap.Blocked);
        addSeries(this.props.historyData.OtherCounts, Resources.OtherName, OutcomeToColorMap.Other);
        var historyData: ChartProps = {
            pageContext: this.context.pageContext,
            chartType: ChartTypesConstants.StackedArea,
            title: Resources.WidgetTitle,
            legend: {
                align: "center",
                enabled: true,
                stackVertically: false
            },
            series: series,
            xAxis: {
                labelFormatMode: "textual",
                labelValues: dates,
                labelsEnabled: true
            },
            yAxis: {
                allowDecimals: false,
                labelFormatMode: "linear",
                labelsEnabled: true,
                markingsEnabled: false,
                max: this.props.historyData.TotalCounts ? Math.max(...this.props.historyData.TotalCounts) : undefined,
                min: 0,
                title: Resources.YAxisTitle
            }
        };

        return historyData;
    }

    private _getCommandBarItems(selectedCommand: PointHistoryCommands): IHeaderCommandBarItem[] {
        return [
            {
                important: true,
                id: "outcomeTrendCommandBarButton",
                text: this._getSelectedCommandText(selectedCommand),
                subMenuProps: 
                {
                    id: 'outcomeTrendSubMenu',
                    items: [
                        {
                            id: 'last14Days',
                            text: Resources.Last14DaysText,
                            onActivate: () => {             
                                this.props.fromDate.value =new Date(new Date().getTime() - 14*Date_Utils.day);
                                this.props.toDate.value = new Date();
                                this.props.onCommandBarSubMenuItemSelected(PointHistoryCommands.Last14Days);
                            }
                        },
                        {
                            id: 'last30Days',
                            text: Resources.Last30DaysText,
                            onActivate: () => {             
                                this.props.fromDate.value =new Date(new Date().getTime() - 30*Date_Utils.day);
                                this.props.toDate.value = new Date();
                                this.props.onCommandBarSubMenuItemSelected(PointHistoryCommands.Last30Days);
                            }
                        },
                        {
                            id: 'customDate',
                            text: Resources.CustomDateText,
                            subMenuProps: {
                                id: 'custom-date-sub-menu',
                                items: [{
                                    id: 'custom-date-from-date',
                                    renderMenuItem: () => {
                                        return (
                                            <div className="custom-date-from-date" role="menuitem">
                                                <div className="heading body-m">
                                                    {Resources.FromText}:
                                                </div>
                                                <DatePicker ariaLabel={Resources.FromText} value={this.props.fromDate} onChange={
                                                    (newDate: Date) => {
                                                        this.props.fromDate.value = newDate;
                                                        this.props.onCommandBarSubMenuItemSelected(PointHistoryCommands.CustomDate);
                                                    }} />
                                            </div>
                                        );
                                    }
                                }, {
                                    id: 'custom-date-to-date',
                                    renderMenuItem: () => {
                                        return (
                                            <div className="custom-date-to-date" role="menuitem">
                                                <div className="heading body-m">
                                                    {Resources.ToText}:
                                                </div>
                                                <DatePicker ariaLabel={Resources.ToText} value={this.props.toDate} onChange={
                                                    (newDate: Date) => {
                                                        this.props.toDate.value = newDate;
                                                        this.props.onCommandBarSubMenuItemSelected(PointHistoryCommands.CustomDate);
                                                    }} />
                                            </div>
                                        );
                                    }
                                }]
                            }
                        }
                    ]
                }
            }
        ];
    }

    private _getSelectedCommandText(selectedCommand: PointHistoryCommands) {
        if(selectedCommand == PointHistoryCommands.Last14Days) {
            return Resources.Last14DaysText;
        }
        if(selectedCommand == PointHistoryCommands.Last30Days) {
           return Resources.Last30DaysText;
        }
        if(selectedCommand == PointHistoryCommands.CustomDate) {
            return this.props.fromDate.value.toLocaleDateString() + " - " + this.props.toDate.value.toLocaleDateString();
        }
    }
}